import React, {FC, PropsWithChildren, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {TAB_BORDER_SIZE} from './tabCommon';

/*
 * Consts.
 */

const TAB_OFFSET_TRANSITION_PERCENT = 0.5;

/*
 * Props.
 */

export interface TabsProps {
  className?: string;
}

/*
 * Style.
 */

const StyledDivWithBorder = styled.div`
  border-bottom: ${TAB_BORDER_SIZE}px solid ${(p) => p.theme.greys.shade30};
`;

const StyledTabContainerDiv = styled.div`
  position: relative;
  overflow-x: hidden;
  padding-bottom: ${TAB_BORDER_SIZE}px;
  margin-bottom: -${TAB_BORDER_SIZE}px;
  box-sizing: border-box;
  height: max-content;
`;

const StyledTabList = styled.div<{$offset: number}>`
  display: inline-flex;
  width: max-content;
  position: relative;
  left: -${(p) => p.$offset}px;

  transition: left 0.3s;

  > * {
    flex-shrink: 0;
  }
`;

const StyledControlContainer = styled.div`
  background-color: ${(p) => p.theme.greys.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const ControlShadeDiv = styled.div<{$direction: 'left' | 'right'}>`
  background: linear-gradient(to ${(p) => p.$direction}, ${(p) => p.theme.greys.white}, transparent);
  width: 22px;
  height: 100%;
`;

const SyledLeftControl = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: calc(100% - ${TAB_BORDER_SIZE}px);
  display: flex;
`;

const StyledRightControlDiv = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: calc(100% - ${TAB_BORDER_SIZE}px);
  display: flex;
`;

/*
 * Component.
 */

export const Tabs: FC<PropsWithChildren<TabsProps>> = (props) => {
  const {className, children} = props;

  const [containerRef, {width: containerWidth}] = useMeasureElement();
  const [listRef, {width: listWidth}] = useMeasureElement();
  const [offsetState, setOffset] = useState(0);

  const hasOverflow = listWidth > containerWidth;

  const maxOffset = listWidth - containerWidth;
  const offset = hasOverflow ? Math.min(offsetState, maxOffset) : 0;

  const isLeftBorder = offset === 0;
  const isRightBorder = offset + containerWidth >= listWidth;

  const onNextPage = useCallback(() => {
    if (isRightBorder) {
      return;
    }
    setOffset(offset + containerWidth * TAB_OFFSET_TRANSITION_PERCENT);
  }, [containerWidth, isRightBorder, offset]);

  const onPreviousPage = useCallback(() => {
    if (isLeftBorder) {
      return;
    }
    setOffset(Math.max(0, offset - containerWidth * TAB_OFFSET_TRANSITION_PERCENT));
  }, [containerWidth, isLeftBorder, offset]);

  const leftControl = useMemo(() => {
    if (isLeftBorder) {
      return null;
    }

    return (
      <SyledLeftControl>
        <StyledControlContainer>
          <IconButton
            iconName="chevronLeftSmall"
            label={<Trans id="tabs-control-previous">Previous</Trans>}
            onClick={onPreviousPage}
          />
        </StyledControlContainer>
        <ControlShadeDiv $direction="right" />
      </SyledLeftControl>
    );
  }, [isLeftBorder, onPreviousPage]);

  const rightControl = useMemo(() => {
    if (isRightBorder) {
      return null;
    }

    return (
      <StyledRightControlDiv>
        <ControlShadeDiv $direction="left" />
        <StyledControlContainer>
          <IconButton
            iconName="chevronRightSmall"
            label={<Trans id="tabs-control-next">Next</Trans>}
            onClick={onNextPage}
          />
        </StyledControlContainer>
      </StyledRightControlDiv>
    );
  }, [isRightBorder, onNextPage]);

  const controls = useMemo(() => {
    if (!hasOverflow) {
      return null;
    }

    return (
      <>
        {leftControl}
        {rightControl}
      </>
    );
  }, [hasOverflow, leftControl, rightControl]);

  return (
    <StyledDivWithBorder className={className}>
      <StyledTabContainerDiv ref={containerRef}>
        <StyledTabList ref={listRef} $offset={offset}>
          {children}
        </StyledTabList>
        {controls}
      </StyledTabContainerDiv>
    </StyledDivWithBorder>
  );
};
